/**
 * Display catalog for dashboard template gallery.
 * Merged with API templates by templateId when available.
 */
export const TEMPLATE_CATALOG = [
  {
    templateId: 1,
    label: "Center Column",
    type: "SCROLL",
    shortDescription: "Centered role-name credits in one column.",
    description:
      "A traditional centered credit roll with roles on the left and names on the right inside a narrow central column. Best for cast lists, smaller crews, and classic end-credit formatting.",
  },
  {
    templateId: 2,
    label: "Dual Column",
    type: "SCROLL",
    shortDescription: "Two columns keep long credits compact.",
    description:
      "Credits are split into two balanced columns across the frame, reducing vertical scroll length without shrinking the type. Best for larger crews, long cast sections, and dense department lists.",
  },
  {
    templateId: 3,
    label: "Side Column",
    type: "SCROLL",
    shortDescription: "Credits placed along one screen side.",
    description:
      "A single credit column sits off to one side of the frame, leaving large negative space for background visuals, footage, or a clean cinematic composition. Best when credits should feel less centered or more editorial.",
  },
  {
    templateId: 4,
    label: "Wide Split",
    type: "SCROLL",
    shortDescription: "Roles and names split across width.",
    description:
      "Roles and names are placed in two distinct vertical columns with a wide gap between them. Best for clean, structured credit rolls where alignment and readability matter more than centered styling.",
  },
  {
    templateId: 5,
    label: "Side Wide Split",
    type: "SCROLL",
    shortDescription: "Split across width along one side.",
    description:
      "Roles sit on one side while names sit far across the frame, creating a clean horizontal separation. Best for minimal layouts where the relationship between role and name should feel structured and spacious.",
  },
  {
    templateId: 6,
    label: "Compact Stack",
    type: "SCROLL",
    shortDescription: "Dense centered list for long rolls.",
    description:
      "A tighter centered layout that keeps credits compact while still grouping sections clearly. Best for long rolls where the goal is to fit more credits without using multiple columns.",
  },
  {
    templateId: 7,
    label: "Center Card",
    type: "CARD",
    shortDescription: "Centered role-name pairs in compact card.",
    description:
      "A clean centered card layout with roles and names arranged in a tight middle block. Best for small credit sections, department cards, or simple end cards that need to feel balanced and readable.",
  },
  {
    templateId: 8,
    label: "Single Main Card",
    type: "CARD",
    shortDescription: "One featured credit centered on screen.",
    description:
      "A minimal card format for highlighting one key credit at a time, such as Directed By, Produced By, Edited By, or Music By. Best for main credits that need individual emphasis.",
  },
  {
    templateId: 9,
    label: "Side Block Card",
    type: "CARD",
    shortDescription: "Credit block aligned toward the left.",
    description:
      "A card layout where the full credit section sits on the right side of the frame, leaving open negative space on the right. Best for editorial layouts, department cards, or credits over background visuals.",
  },
  {
    templateId: 10,
    label: "Dual Column Card",
    type: "CARD",
    shortDescription: "Two credit blocks across the frame.",
    description:
      "A two-section card layout that places credit groups on both sides of the frame. Best for showing two departments, two short crew sections, or parallel role-name lists in one static card.",
  },
  {
    templateId: 11,
    label: "Wide Split Card",
    type: "CARD",
    shortDescription: "Roles left, names right across frame.",
    description:
      "A spacious card layout where roles and names are separated across the width of the screen. Best for clean department cards where alignment and readability matter more than compactness.",
  },
  {
    templateId: 12,
    label: "Side Stack Card",
    type: "CARD",
    shortDescription: "Stacked credits placed on one side.",
    description:
      "A vertically stacked card layout positioned near the side of the frame. Best for shorter department lists, lower-key end cards, or layouts that leave room for imagery, logos, or background footage.",
  },
];

/**
 * Local preview path for template thumbnails/modals.
 * Always uses public assets: Client/public/template-preview/image{1-12}-new.png
 */
export function getLocalTemplatePreviewSrc(templateOrId) {
  const id = Number(
    typeof templateOrId === "object" && templateOrId !== null
      ? templateOrId.templateId ?? templateOrId.id
      : templateOrId,
  );
  if (!id || id < 1 || id > 12) return null;
  return `/template-preview/image${id}-new.png`;
}

/**
 * Merge catalog entries with API templates by templateId.
 * Catalog order (1–12) wins; preview images always come from local public assets.
 */
export function mergeTemplatesWithCatalog(apiTemplates = []) {
  const byId = new Map(
    (apiTemplates || []).map((t) => [Number(t.templateId ?? t.id), t]),
  );

  return TEMPLATE_CATALOG.map((entry) => {
    const api = byId.get(entry.templateId);
    return {
      ...entry,
      ...api,
      // Always use local public preview assets (not API ImgUrl)
      ImgUrl: getLocalTemplatePreviewSrc(entry.templateId),
      templateId: entry.templateId,
      id: entry.templateId,
      label: entry.label,
      type: entry.type,
      shortDescription: entry.shortDescription,
      description: entry.description,
    };
  });
}
